'use client';

import React, { useState, useEffect, lazy, Suspense, useMemo, useCallback } from 'react';
import { reduxLogger } from '@/core/utils/logger';
import { useSearchParams } from 'next/navigation';
import { useAppNavigation } from '@/core/hooks/useAppNavigation';
import { Menu, ShieldAlert, Settings } from 'lucide-react';
import SettingsSidebar from '../components/SettingsSidebar/SettingsSidebar';
import { ErrorBoundary } from '@/shared/components/error/ErrorBoundary';
import { showToast } from '@/shared/utils/toast';
import { Skeleton } from '@/shared/components/skeletons';

// Import the most common settings components directly for faster initial load
import PersonalInformation from '../components/PersonalInformation/PersonalInformation';
import YourBusiness from '../components/YourBusiness/YourBusiness';
import PhoneNumber from '../components/PhoneNumber/PhoneNumber';

// Lazy load less frequently used components
const TeamManagement = lazy(() => import('../components/TeamManagement/TeamManagement'));
const TagsManagement = lazy(() => import('../components/TagsManagement/TagsManagement'));
const TemplatesManagement = lazy(() => import('../components/TemplatesManagement/TemplatesManagement'));
const AutoLogging = lazy(() => import('../components/AutoLogging/AutoLogging'));
const LoggingHistory = lazy(() => import('../components/LoggingHistory/LoggingHistory'));
const Subscription = lazy(() => import('../components/Subscription/Subscription'));
const ArchiveDownloading = lazy(() => import('../components/ArchiveDownloading/ArchiveDownloading'));
const AuditLog = lazy(() => import('../components/AuditLog/AuditLog'));
const ExternalSystems = lazy(() => import('../components/ExternalSystems/ExternalSystemsWrapper'));
const ToolsSettings = lazy(() => import('../components/ToolsSettings/ToolsSettings'));
import { useAppSelector } from '@/core/store/hooks';
import { useIntegrationStatus } from '@/api/features/integrations';
import { usePrefetchCoordinator } from '@/core/hooks/usePrefetchCoordinator';
import { isAdmin as checkIsAdmin } from '@/core/auth/utils/roleChecks';

const SettingsContainer: React.FC = () => {
  const { navigate } = useAppNavigation();
  const searchParams = useSearchParams();
  const sectionParam = searchParams.get('section');

  // Default to 'profile' if no section is specified
  const [activeSection, setActiveSection] = useState<string>(sectionParam || 'profile');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Check if user is admin (memoized to prevent unnecessary re-renders)
  const userProfile = useAppSelector(state => state.userProfile.session);
  const isAdmin = useMemo(() => checkIsAdmin(userProfile), [userProfile]);

  // Get integration status with built-in safety checks
  const { isHawksoftConnected } = useIntegrationStatus();

  // Use coordinated prefetch - no duplicates, proper timing
  usePrefetchCoordinator({
    enabled: true,
    priority: 'high',
    section: 'settings'
  });

  // Memoize admin sections to prevent unnecessary re-creation
  const adminSections = useMemo(() => ['agency', 'phone', 'subscription', 'integrations', 'tools', 'audit-log'], []);

  // Update active section when URL param changes
  useEffect(() => {
    if (sectionParam) {
      setActiveSection(sectionParam);
    }
  }, [sectionParam]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      // Auto-close sidebar on mobile/tablet, auto-open on desktop
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };

    // Set initial state
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle section change (memoized to prevent unnecessary re-renders)
  const handleSectionChange = useCallback((section: string) => {
    setActiveSection(section);
    navigate(`/settings?section=${section}`);
  }, [navigate]);

  // Memoize sidebar handlers to prevent unnecessary re-renders
  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  // Memoize section labels to prevent unnecessary re-creation
  const sectionLabels = useMemo<Record<string, string>>(() => ({
    profile: 'Profile & Display',
    business: 'Your Business',
    phone: 'Phone Number',
    tags: 'Tag Management',
    templates: 'Message Templates',
    logging: 'Message Logging',
    archive: 'Archive & Download',
    agency: 'Agency Management',
    subscription: 'Subscription & Billing',
    integrations: 'Integrations',
    tools: 'Tools',
    'audit-log': 'Audit Log',
  }), []);

  const getSectionLabel = useCallback((section: string) => {
    return sectionLabels[section] || 'Settings';
  }, [sectionLabels]);

  // Custom error fallback for settings components
  const SettingsErrorFallback = (error: Error, errorInfo: { componentStack?: string }, retry: () => void) => (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
      <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
        <ShieldAlert className="h-8 w-8 text-red-500" />
      </div>
      <h2 className="text-xl font-semibold mb-2 text-text-primary">Settings Error</h2>
      <p className="text-text-secondary mb-4 text-center max-w-md">
        There was an error loading this settings section. This might be due to a network issue or temporary problem.
      </p>
      <div className="flex space-x-3">
        <button
          onClick={retry}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
        >
          Try Again
        </button>
        <button
          onClick={() => {
            showToast.info('Refreshing settings page...');
            window.location.reload();
          }}
          className="px-4 py-2 border border-border rounded-md text-text-primary hover:bg-hover transition-colors"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );

  // Render the appropriate settings component based on active section
  const renderSettingsContent = () => {
    // Check if the current section requires admin permissions
    const needsAdminPermission = adminSections.includes(activeSection);

    // If it requires admin permissions and user is not admin, show access denied
    if (needsAdminPermission && !isAdmin) {
      return <AccessDenied />;
    }

    // Otherwise, render the appropriate content based on section
    switch (activeSection) {
      case 'profile':
        return <PersonalInformation />;
      case 'business':
        return <YourBusiness />;
      case 'phone':
        return <PhoneNumber />;
      case 'tags':
        return (
          <Suspense fallback={<SettingsLoader />}>
            <TagsManagement />
          </Suspense>
        );
      case 'templates':
        return (
          <Suspense fallback={<SettingsLoader />}>
            <TemplatesManagement />
          </Suspense>
        );
      case 'logging':
        if (isHawksoftConnected) {
          return (
            <Suspense fallback={<SettingsLoader />}>
              <div>
                <AutoLogging />
                <LoggingHistory />
              </div>
            </Suspense>
          );
        }
        return <ComingSoon title="Logging" />;
      case 'archive':
        return (
          <Suspense fallback={<SettingsLoader />}>
            <ArchiveDownloading />
          </Suspense>
        );
      case 'agency':
        return (
          <Suspense fallback={<SettingsLoader />}>
            <TeamManagement />
          </Suspense>
        );
      case 'subscription':
        return (
          <Suspense fallback={<SettingsLoader />}>
            <Subscription />
          </Suspense>
        );
      case 'integrations':
        return (
          <Suspense fallback={<SettingsLoader />}>
            <ExternalSystems />
          </Suspense>
        );
      case 'tools':
        return (
          <Suspense fallback={<SettingsLoader />}>
            <ToolsSettings />
          </Suspense>
        );
      case 'audit-log':
        return (
          <Suspense fallback={<SettingsLoader />}>
            <AuditLog />
          </Suspense>
        );
      default:
        return <PersonalInformation />;
    }
  };

  // Loading component for lazy-loaded sections
  const SettingsLoader = () => (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header skeleton */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
      </div>

      {/* Form card skeleton */}
      <div className="bg-card-background rounded-lg shadow-sm border border-border p-4 sm:p-6">
        <div className="flex items-center mb-6">
          <Skeleton className="h-5 w-5 rounded mr-2" />
          <Skeleton className="h-6 w-48" />
        </div>

        {/* Form fields skeleton */}
        <div className="space-y-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full max-w-md" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full w-full flex overflow-hidden rounded-2xl relative" data-testid="settings-container">
      {/* Settings Sidebar */}
      <SettingsSidebar
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
      />

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto bg-background flex flex-col min-w-0" data-testid="settings-content">
        {/* Mobile header with toggle button */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-border bg-card-bg">
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-hover text-text-secondary"
              aria-label="Toggle settings menu"
            >
              <Menu size={20} />
            </button>
            <h1 className="text-lg font-semibold text-text-primary">
              {getSectionLabel(activeSection)}
            </h1>
          </div>
        </div>

        {/* Content container */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <ErrorBoundary
              fallback={SettingsErrorFallback}
              onError={(error, errorInfo) => {
                reduxLogger.error('Settings component error:', error, errorInfo);
                showToast.error('Settings section failed to load');
              }}
            >
              {renderSettingsContent()}
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder components
const ComingSoon: React.FC<{ title: string }> = ({ title }) => (
  <div className="bg-card-background rounded-lg shadow-sm border border-border p-8 text-center">
    <div className="w-16 h-16 mx-auto mb-4 bg-surface rounded-full flex items-center justify-center">
      <Settings className="h-8 w-8 text-text-secondary" />
    </div>
    <h2 className="text-2xl font-semibold mb-4 text-text-primary">Connect to your AMS</h2>
    <p className="text-text-secondary">Connect to your Agency Management System to unlock {title}.</p>
  </div>
);

const AccessDenied: React.FC = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 text-center">
    <div className="flex justify-center mb-4">
      <ShieldAlert size={48} className="text-amber-500" />
    </div>
    <h2 className="text-2xl font-semibold mb-4 text-red-600">Admin Access Required</h2>
    <p className="text-gray-600">This section is only available to administrators.</p>
    <p className="text-gray-600 mt-2">Please contact your administrator for assistance.</p>
  </div>
);

export default SettingsContainer;
